import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import {
  Users, BookOpen, GraduationCap, ClipboardList, Award, FileText,
  TrendingUp, Sparkles, BarChart3, Activity,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

export default function Dashboard() {
  const { ctx } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/dashboard", { params: { stage: ctx.stage, year: ctx.year } })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [ctx.stage, ctx.year]);

  const gradeChartData = Object.entries(data?.grade_distribution || {})
    .map(([grade, count]) => ({ grade, count }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PageHeader
        icon={Sparkles}
        overline="لوحة القيادة"
        title={ctx.stage}
        subtitle={`نظرة تنفيذية شاملة على بيانات المدرسة، مؤشرات الأداء، والأنشطة الأخيرة للعام ${ctx.year}`}
        badge={ctx.year}
        stats={[
          { label: "طلاب مسجّلون",  value: (data?.students_count ?? 0).toLocaleString("ar-EG") },
          { label: "مواد دراسية",   value: data?.subjects_count ?? "—" },
          { label: "معلّمون نشطون",  value: data?.teachers_count ?? "—" },
          { label: "شهادات صادرة",  value: data?.certificates_count ?? "—" },
        ]}
      />

      {/* Secondary stat grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 stagger">
        <StatCard icon={Users}          label="الطلاب"     value={data?.students_count ?? "—"}        accent="primary"  testId="stat-students" />
        <StatCard icon={BookOpen}       label="المواد"     value={data?.subjects_count ?? "—"}        accent="teal"     testId="stat-subjects" />
        <StatCard icon={GraduationCap}  label="الفصول"     value={data?.classes_count ?? "—"}         accent="emerald"  testId="stat-classes" />
        <StatCard icon={Users}          label="المعلّمون"  value={data?.teachers_count ?? "—"}        accent="slate"    testId="stat-teachers" />
        <StatCard icon={ClipboardList}  label="القوالب"    value={data?.templates_count ?? "—"}       accent="gold"     testId="stat-templates" />
        <StatCard icon={Award}          label="الشهادات"   value={data?.certificates_count ?? "—"}    accent="rose"     testId="stat-certificates" />
      </div>

      {/* Chart + activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Chart card */}
        <div className="lg:col-span-2 card-premium">
          <div
            className="px-6 py-5 flex items-center justify-between"
            style={{ borderBottom: "1px solid #EEF3EF" }}
          >
            <div className="flex items-center gap-3">
              <div className="icon-chip h-10 w-10">
                <BarChart3 className="h-4 w-4" strokeWidth={1.75} />
              </div>
              <div>
                <div className="text-[11px] font-medium text-slate-400 tracking-[0.10em] uppercase mb-0.5">
                  إحصاءات
                </div>
                <h3 className="font-display text-[15.5px] font-bold text-slate-900 tracking-tight">
                  توزيع الطلاب حسب الصف
                </h3>
              </div>
            </div>
            <span
              className="text-[10.5px] font-bold px-3 py-1.5 rounded-full tabular-nums"
              style={{ background: "rgba(14, 116, 105, 0.08)", color: "#0E7469" }}
            >
              {ctx.year}
            </span>
          </div>
          <div className="p-6 h-72">
            {gradeChartData.length === 0 ? (
              <EmptyChart loading={loading} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={gradeChartData} margin={{ top: 12, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid stroke="#EEF3EF" strokeDasharray="0" vertical={false} />
                  <XAxis
                    dataKey="grade" fontSize={11} tickLine={false} axisLine={false}
                    interval={0} angle={-15} textAnchor="end" height={70} stroke="#94A3B8"
                  />
                  <YAxis fontSize={11} tickLine={false} axisLine={false} width={30} stroke="#94A3B8" />
                  <Tooltip
                    cursor={{ fill: "rgba(14, 116, 105, 0.06)" }}
                    contentStyle={{
                      background: "#FFFFFF",
                      border: "1px solid #E4E9E5",
                      borderRadius: "10px",
                      fontSize: "12px",
                      boxShadow: "0 8px 24px -6px rgba(14, 116, 105, 0.15)",
                      padding: "8px 12px",
                    }}
                    labelStyle={{ color: "#0F172A", fontWeight: 600 }}
                  />
                  <defs>
                    <linearGradient id="barPremium" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#14B8A6" />
                      <stop offset="100%" stopColor="#0E7469" />
                    </linearGradient>
                  </defs>
                  <Bar dataKey="count" fill="url(#barPremium)" radius={[6, 6, 0, 0]} maxBarSize={42} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Activity card */}
        <div className="card-premium flex flex-col">
          <div
            className="px-6 py-5 flex items-center gap-3"
            style={{ borderBottom: "1px solid #EEF3EF" }}
          >
            <div className="icon-chip icon-chip-gold h-10 w-10">
              <Activity className="h-4 w-4" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-[11px] font-medium text-slate-400 tracking-[0.10em] uppercase mb-0.5">
                النشاط
              </div>
              <h3 className="font-display text-[15.5px] font-bold text-slate-900 tracking-tight">
                آخر النشاطات
              </h3>
            </div>
          </div>
          <div className="flex-1 max-h-[300px] overflow-y-auto" data-testid="recent-activity">
            {(!data?.recent_activity || data.recent_activity.length === 0) ? (
              <div className="text-[12.5px] text-slate-400 py-10 text-center px-4">
                <TrendingUp className="h-5 w-5 mx-auto mb-2 text-slate-300" strokeWidth={1.5} />
                لا توجد أنشطة بعد
              </div>
            ) : (
              data.recent_activity.map((a, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 px-6 py-3.5 hover:bg-slate-50/50 transition-colors"
                  style={{
                    borderBottom: i < data.recent_activity.length - 1 ? "1px solid #F5F8F5" : "none",
                  }}
                >
                  <div className="icon-chip h-8 w-8 shrink-0 mt-0.5 rounded-lg">
                    <FileText className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] text-slate-700 leading-snug">{a.message}</div>
                    <div className="text-[10.5px] text-slate-400 mt-1 tabular-nums">
                      {new Date(a.created_at).toLocaleString("ar-EG")}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyChart({ loading }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center">
      <div className="icon-chip h-14 w-14 mb-3 rounded-2xl">
        <BookOpen className="h-6 w-6" strokeWidth={1.5} />
      </div>
      <div className="text-[13px] text-slate-600 font-medium">
        {loading ? "جارٍ تحميل البيانات…" : "لا توجد بيانات لعرضها"}
      </div>
    </div>
  );
}
